import type {
  ControlBridgeStructureNode,
  KryspaceControlBridgeStructure,
} from '../../../types/kryspaceControlBridgeStructure';
import type { EntityDetailChildCompanyArtifact } from '../../../types/kryspaceEntityDetail';
import {
  normalizeScopeIdKey,
  queueItemMatchesScopeId,
  scopeIdsEqual,
} from '../../../utils/scopeIdPlane';

/** Prefer CA / UUID for artifact-detail; everything else (ai-*, co-*) as item-detail. */
export function structureNavOpenKind(id: string): 'artifact' | 'item' {
  const s = id.trim();
  if (!s) return 'artifact';
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) {
    return 'artifact';
  }
  if (s.toLowerCase().startsWith('ca-')) return 'artifact';
  return 'item';
}

export function isControlBridgeRootArtifact(
  artifactInstanceId: string,
  artifactTypeRef: string | undefined,
  structure: KryspaceControlBridgeStructure | undefined
): boolean {
  const id = artifactInstanceId.trim();
  if (!id) return false;
  if (structure?.rootInstanceId && structure.rootInstanceId.trim() === id) {
    return true;
  }
  const idKey = id.toLowerCase();
  // DDD / thin-read often surfaces the CB as an item (ai-*-control-bridge-op) while
  // structure.rootInstanceId is missing or only the ca-* twin exists.
  if (
    idKey.includes('control-bridge') ||
    idKey.includes('controlbridge') ||
    /^ca-.+-root$/i.test(id)
  ) {
    return true;
  }
  const t = (artifactTypeRef || '').toLowerCase().replace(/_/g, '-');
  return (
    t.includes('control-bridge') ||
    t.includes('controlbridge') ||
    t === 'companycontrolbridge' ||
    t === 'pt-company-control-bridge'
  );
}

export function structureNodeNavId(node: ControlBridgeStructureNode): string {
  return (node.filterScope || node.artifactInstanceId || node.key || '').trim();
}

function nodeToNavItem(node: ControlBridgeStructureNode): EntityDetailChildCompanyArtifact | null {
  const id = structureNodeNavId(node);
  if (!id) return null;
  return {
    artifactInstanceId: id,
    name: node.label || id,
    artifactTypeRef: node.artifactTypeRef || '',
    currentState: '',
  };
}

/**
 * Top-level structure nodes (domains / bridges / specialty) for nested-panel pick list.
 * Omits top-level subdomain role rows; nested subdomain children are handled separately.
 */
export function structureTopLevelDomainNavItems(
  structure: KryspaceControlBridgeStructure | undefined
): EntityDetailChildCompanyArtifact[] {
  if (!structure?.groups?.length) return [];
  const out: EntityDetailChildCompanyArtifact[] = [];
  const seen = new Set<string>();
  for (const group of structure.groups) {
    for (const node of group.nodes ?? []) {
      if ((node.role || '').toLowerCase() === 'subdomain') continue;
      const item = nodeToNavItem(node);
      if (!item || seen.has(item.artifactInstanceId)) continue;
      seen.add(item.artifactInstanceId);
      out.push(item);
    }
  }
  return out;
}

function walkStructureNodes(
  structure: KryspaceControlBridgeStructure | undefined,
  visit: (node: ControlBridgeStructureNode, parent: ControlBridgeStructureNode | null) => boolean
): ControlBridgeStructureNode | undefined {
  if (!structure?.groups?.length) return undefined;
  type Frame = { node: ControlBridgeStructureNode; parent: ControlBridgeStructureNode | null };
  const stack: Frame[] = [];
  for (const group of structure.groups) {
    for (const node of group.nodes ?? []) {
      stack.push({ node, parent: null });
    }
  }
  while (stack.length) {
    const { node, parent } = stack.pop()!;
    if (visit(node, parent)) return node;
    for (const child of node.children ?? []) {
      stack.push({ node: child, parent: node });
    }
  }
  return undefined;
}

export function findStructureNode(
  structure: KryspaceControlBridgeStructure | undefined,
  entityId: string
): ControlBridgeStructureNode | undefined {
  const want = entityId.trim();
  if (!want) return undefined;
  return walkStructureNodes(structure, (node) => structureNodeNavId(node) === want);
}

/** Immediate structure-tree parent of ``entityId``, if any. */
export function findStructureParentNav(
  structure: KryspaceControlBridgeStructure | undefined,
  entityId: string
): { id: string; label: string } | null {
  const want = entityId.trim();
  if (!want) return null;
  let found: { id: string; label: string } | null = null;
  walkStructureNodes(structure, (node, parent) => {
    if (structureNodeNavId(node) !== want || !parent) return false;
    const id = structureNodeNavId(parent);
    if (!id) return false;
    found = { id, label: parent.label || id };
    return true;
  });
  return found;
}

/** Direct children of a structure node (includes subdomain role). */
export function structureChildNavItems(
  structure: KryspaceControlBridgeStructure | undefined,
  entityId: string
): EntityDetailChildCompanyArtifact[] {
  const node = findStructureNode(structure, entityId);
  if (!node?.children?.length) return [];
  const out: EntityDetailChildCompanyArtifact[] = [];
  const seen = new Set<string>();
  for (const child of node.children) {
    const item = nodeToNavItem(child);
    if (!item || seen.has(item.artifactInstanceId)) continue;
    seen.add(item.artifactInstanceId);
    out.push(item);
  }
  return out;
}

/** Entity id + descendant structure filterScopes (for queue matching). */
export function structureScopeClosureIds(
  structure: KryspaceControlBridgeStructure | undefined,
  entityId: string
): string[] {
  const root = entityId.trim();
  if (!root) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (raw: string) => {
    const key = normalizeScopeIdKey(raw);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(raw.trim());
  };
  add(root);
  const node = findStructureNode(structure, root);
  if (!node) return out;
  const stack = [...(node.children ?? [])];
  while (stack.length) {
    const child = stack.pop()!;
    add(structureNodeNavId(child));
    for (const grand of child.children ?? []) stack.push(grand);
  }
  return out;
}

export type EntityDetailQueueLike = {
  itemId?: string;
  title?: string;
  name?: string;
  filterScope?: string;
  domain?: string;
  statusPhrase?: string;
  artifactTypeRef?: string;
};

export function queueItemsForEntityScope(args: {
  structure?: KryspaceControlBridgeStructure;
  entityId: string;
  artifactTypeRef?: string;
  queueItems: EntityDetailQueueLike[];
}): EntityDetailChildCompanyArtifact[] {
  const queue = args.queueItems ?? [];
  if (!queue.length) return [];
  const isCbRoot = isControlBridgeRootArtifact(
    args.entityId,
    args.artifactTypeRef,
    args.structure
  );
  const scoped = isCbRoot
    ? queue
    : queue.filter((item) => {
        const closure = structureScopeClosureIds(args.structure, args.entityId);
        if (closure.some((scopeId) => queueItemMatchesScopeId(item, scopeId))) {
          return true;
        }
        return scopeIdsEqual(item.itemId, args.entityId);
      });

  const out: EntityDetailChildCompanyArtifact[] = [];
  const seen = new Set<string>();
  for (const item of scoped) {
    const id = (item.itemId || '').trim();
    if (!id || seen.has(id)) continue;
    if (scopeIdsEqual(id, args.entityId)) continue;
    seen.add(id);
    out.push({
      artifactInstanceId: id,
      name: (item.title || item.name || id).trim() || id,
      artifactTypeRef: item.artifactTypeRef || '',
      currentState: (item.statusPhrase || '').trim(),
    });
  }
  return out;
}

export type StructureNavResolution = {
  items: EntityDetailChildCompanyArtifact[];
  sectionTitle: string;
  /** When ``work``, always open via item-detail (eligible activities). */
  openMode?: 'work' | 'structure';
};

function childCasLookLikeDomainScopes(
  childCas: EntityDetailChildCompanyArtifact[]
): boolean {
  return childCas.some((row) => {
    const t = (row.artifactTypeRef || '').toLowerCase().replace(/_/g, '-');
    if (!t.includes('domain')) return false;
    if (t.includes('control-bridge') || t.includes('controlbridge')) return false;
    return true;
  });
}

/**
 * Nested-panel pick list:
 * 1. Eligible work in this scope (CB root = company-wide queue — what operators pick next)
 * 2. Real domain CA children (Fred) only when there is no queue work
 *
 * Does **not** list structure domain/subdomain scaffolding (Strategy Subdomain, …).
 * Empty queue on CB root returns an empty Eligible work section so the panel never
 * disguises structure scopes as a work tree.
 */
export function resolveStructureNavList(args: {
  structure?: KryspaceControlBridgeStructure;
  entityId: string;
  artifactTypeRef?: string;
  childCompanyArtifacts?: EntityDetailChildCompanyArtifact[];
  queueItems?: EntityDetailQueueLike[];
}): StructureNavResolution | null {
  const childCas = args.childCompanyArtifacts ?? [];
  const isCbRoot = isControlBridgeRootArtifact(
    args.entityId,
    args.artifactTypeRef,
    args.structure
  );

  const work = queueItemsForEntityScope({
    structure: args.structure,
    entityId: args.entityId,
    artifactTypeRef: args.artifactTypeRef,
    queueItems: args.queueItems ?? [],
  });
  if (work.length > 0) {
    return { items: work, sectionTitle: 'Eligible work', openMode: 'work' };
  }

  if (isCbRoot) {
    // Prefer empty Eligible work over Structure scopes / non-domain CA stubs.
    if (childCas.length > 0 && childCasLookLikeDomainScopes(childCas)) {
      return {
        items: childCas,
        sectionTitle: 'Child company artifacts',
        openMode: 'structure',
      };
    }
    return { items: [], sectionTitle: 'Eligible work', openMode: 'work' };
  }

  if (childCas.length > 0) {
    return {
      items: childCas,
      sectionTitle: 'Child company artifacts',
      openMode: 'structure',
    };
  }

  return null;
}

/**
 * Parent: structure-tree parent first; then payload parent; then CB root (opt-in fallback).
 */
export function resolveParentNavTarget(args: {
  entityId?: string | null;
  parentArtifactInstanceId?: string | null;
  parentArtifactLabel?: string | null;
  structure?: KryspaceControlBridgeStructure;
  allowControlBridgeRootFallback?: boolean;
}): { id: string; label: string } | null {
  const entityId = (args.entityId || '').trim();
  if (entityId) {
    const structureParent = findStructureParentNav(args.structure, entityId);
    if (structureParent) return structureParent;
  }
  const parentId = (args.parentArtifactInstanceId || '').trim();
  if (parentId) {
    return {
      id: parentId,
      label: (args.parentArtifactLabel || '').trim() || parentId,
    };
  }
  if (!args.allowControlBridgeRootFallback) return null;
  const rootId = (args.structure?.rootInstanceId || '').trim();
  if (!rootId) return null;
  return {
    id: rootId,
    label: (args.structure?.companyTitle || '').trim() || 'Control Bridge',
  };
}

/** Scope key when navigating to a parent: CB root → company; else parent id. */
export function structureParentScopeKey(
  parentId: string,
  structure?: KryspaceControlBridgeStructure
): string {
  const id = parentId.trim();
  const rootId = (structure?.rootInstanceId || '').trim();
  if (rootId && id === rootId) return 'company';
  if (isControlBridgeRootArtifact(id, undefined, structure)) return 'company';
  return id;
}
